import { useEffect, useMemo, useState } from 'react';
import { Calculator, ClipboardList, FileCheck, Clock3, LineChart, TrendingUp, Settings2, X } from 'lucide-react';
import PageContainer from '../components/PageContainer';
import DriveValueTrendChart from '../components/DriveValueTrendChart';

const defaultPayload = {
  config: {
    technicianHoursPerForm: 0.1,
    managementHoursPerReport: 2,
    technicianRate: 290,
    managementRate: 430,
    notes: '',
  },
  months: [],
};

const cardBaseClasses =
  'rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-lg shadow-slate-900/5 dark:border-white/10 dark:bg-slate-900/70';

const currencyFormatter = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const hourFormatter = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function KpiCard({ title, value, subtitle, icon: Icon }) {
  return (
    <article className={cardBaseClasses}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">{title}</p>
          <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{value}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{subtitle}</p>
        </div>
        <span className="rounded-xl bg-blue-500/10 p-2 text-blue-600 dark:bg-blue-400/10 dark:text-blue-300">
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </article>
  );
}

export default function DriveValueEngine() {
  const [payload, setPayload] = useState(defaultPayload);
  const [draftConfig, setDraftConfig] = useState(defaultPayload.config);
  const [setupOpen, setSetupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch('/api/drive_value.php');
        if (!response.ok) throw new Error('Failed to load DRiVE data');
        const data = await response.json();
        setPayload({
          config: { ...defaultPayload.config, ...(data.config || {}) },
          months: Array.isArray(data.months) ? data.months : [],
        });
      } catch (err) {
        console.error(err);
        setError('Could not load shared DRiVE values. Showing defaults.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    setDraftConfig(payload.config);
  }, [payload.config]);

  const computed = useMemo(() => {
    const totalForms = payload.months.reduce((sum, month) => sum + (Number(month.forms) || 0), 0);
    const totalReports = payload.months.reduce((sum, month) => sum + (Number(month.reports) || 0), 0);

    const technicianHoursSaved = totalForms * Number(payload.config.technicianHoursPerForm || 0);
    const managementVisibilityGains = totalReports * Number(payload.config.managementHoursPerReport || 0);
    const formValueAdd = technicianHoursSaved * Number(payload.config.technicianRate || 0);
    const reportValueAdd = managementVisibilityGains * Number(payload.config.managementRate || 0);
    const totalValueAdd = formValueAdd + reportValueAdd;

    const monthlyTrend = payload.months.map((month) => {
      const forms = Number(month.forms) || 0;
      const reports = Number(month.reports) || 0;
      const monthTechHours = forms * Number(payload.config.technicianHoursPerForm || 0);
      const monthMgmtHours = reports * Number(payload.config.managementHoursPerReport || 0);
      const monthTotal = monthTechHours * Number(payload.config.technicianRate || 0)
        + monthMgmtHours * Number(payload.config.managementRate || 0);

      return {
        month: month.month,
        totalValueAdd: Number(monthTotal.toFixed(2)),
      };
    });

    return {
      totalForms,
      totalReports,
      technicianHoursSaved,
      managementVisibilityGains,
      formValueAdd,
      reportValueAdd,
      totalValueAdd,
      monthlyTrend,
    };
  }, [payload]);

  const saveSetup = async () => {
    setSaving(true);
    setError('');
    try {
      const normalized = {
        technicianHoursPerForm: Math.max(0, Number(draftConfig.technicianHoursPerForm || 0)),
        managementHoursPerReport: Math.max(0, Number(draftConfig.managementHoursPerReport || 0)),
        technicianRate: Math.max(0, Number(draftConfig.technicianRate || 0)),
        managementRate: Math.max(0, Number(draftConfig.managementRate || 0)),
        notes: `${draftConfig.notes || ''}`,
      };

      const response = await fetch('/api/drive_value.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: normalized }),
      });

      if (!response.ok) throw new Error('Save failed');

      setPayload((prev) => ({ ...prev, config: normalized }));
      setSetupOpen(false);
    } catch (err) {
      console.error(err);
      setError('Could not save DRiVE setup values. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer className="relative overflow-hidden bg-slate-950 text-slate-100">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.14),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.14),transparent_55%)]" />
      <div className="relative z-10 flex flex-1 flex-col gap-6">
        <header className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white">DRiVE</h1>
              <p className="mt-1 text-sm text-slate-300">DRi Value Engine for cost savings and visibility gains.</p>
            </div>
            <button
              type="button"
              onClick={() => setSetupOpen(true)}
              className="inline-flex items-center gap-2 self-start rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-950 transition hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300/70"
            >
              <Settings2 className="h-4 w-4" /> Value Setup
            </button>
          </div>
        </header>

        {error && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <KpiCard
            title="Total Value Add"
            value={`R ${currencyFormatter.format(computed.totalValueAdd)}`}
            subtitle="Combined value from form and report gains"
            icon={Calculator}
          />
          <KpiCard
            title="Total Forms"
            value={integerFormatter.format(computed.totalForms)}
            subtitle="Total forms in demo monthly dataset"
            icon={ClipboardList}
          />
          <KpiCard
            title="Total Reports"
            value={integerFormatter.format(computed.totalReports)}
            subtitle="Total reports in demo monthly dataset"
            icon={FileCheck}
          />
          <KpiCard
            title="Technician Hours Saved"
            value={`${hourFormatter.format(computed.technicianHoursSaved)} hrs`}
            subtitle={`Total Forms × ${payload.config.technicianHoursPerForm} hrs/form`}
            icon={Clock3}
          />
          <KpiCard
            title="Management Visibility Gains"
            value={`${hourFormatter.format(computed.managementVisibilityGains)} hrs`}
            subtitle={`Total Reports × ${payload.config.managementHoursPerReport} hrs/report`}
            icon={LineChart}
          />
          <KpiCard
            title="Form Value Add"
            value={`R ${currencyFormatter.format(computed.formValueAdd)}`}
            subtitle={`Technician Hours Saved × R ${currencyFormatter.format(payload.config.technicianRate)}/hr`}
            icon={TrendingUp}
          />
          <KpiCard
            title="Report Value Add"
            value={`R ${currencyFormatter.format(computed.reportValueAdd)}`}
            subtitle={`Management Visibility Gains × R ${currencyFormatter.format(payload.config.managementRate)}/hr`}
            icon={TrendingUp}
          />
        </div>

        <section className={`${cardBaseClasses} min-h-[350px]`}>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Total Value Add Trend</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Monthly demo trend (ZAR)</p>
          <div className="mt-4 h-72">
            <DriveValueTrendChart data={computed.monthlyTrend} />
          </div>
        </section>

        <section className={cardBaseClasses}>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">Value Setup Notes</h2>
          <p className="mt-2 whitespace-pre-line text-sm text-slate-700 dark:text-slate-300">
            {payload.config.notes || 'No notes saved yet.'}
          </p>
        </section>
      </div>

      {setupOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-slate-900 p-5 shadow-2xl shadow-black/40">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-2xl font-semibold text-white">Value Setup</h3>
                <p className="text-sm text-slate-300">These shared settings update DRiVE values for all users.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDraftConfig(payload.config);
                  setSetupOpen(false);
                }}
                className="rounded-lg border border-white/10 bg-white/5 p-2 text-slate-200 transition hover:bg-white/10"
                aria-label="Close value setup"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">Technician Hours per Form (hours)</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={draftConfig.technicianHoursPerForm}
                  onChange={(event) => setDraftConfig((prev) => ({ ...prev, technicianHoursPerForm: event.target.value }))}
                  className="w-full rounded-lg border border-white/15 bg-slate-950/70 px-3 py-2 text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">Management Hours per Report (hours)</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={draftConfig.managementHoursPerReport}
                  onChange={(event) => setDraftConfig((prev) => ({ ...prev, managementHoursPerReport: event.target.value }))}
                  className="w-full rounded-lg border border-white/15 bg-slate-950/70 px-3 py-2 text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">Technician Rate (ZAR/hour)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={draftConfig.technicianRate}
                  onChange={(event) => setDraftConfig((prev) => ({ ...prev, technicianRate: event.target.value }))}
                  className="w-full rounded-lg border border-white/15 bg-slate-950/70 px-3 py-2 text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">Management Rate (ZAR/hour)</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={draftConfig.managementRate}
                  onChange={(event) => setDraftConfig((prev) => ({ ...prev, managementRate: event.target.value }))}
                  className="w-full rounded-lg border border-white/15 bg-slate-950/70 px-3 py-2 text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>

              <label className="space-y-2 sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">Notes</span>
                <textarea
                  value={draftConfig.notes}
                  onChange={(event) => setDraftConfig((prev) => ({ ...prev, notes: event.target.value }))}
                  rows={4}
                  className="w-full rounded-lg border border-white/15 bg-slate-950/70 px-3 py-2 text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setDraftConfig(payload.config);
                  setSetupOpen(false);
                }}
                className="rounded-lg border border-white/20 bg-white/5 px-4 py-2 font-medium text-slate-200 transition hover:bg-white/10"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveSetup}
                className="rounded-lg bg-emerald-500 px-4 py-2 font-semibold text-emerald-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={saving || loading}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
